<template src="./reviews-list.html"></template>

<script lang="ts">
import { nextTick } from 'vue'
import { debounce } from 'util/ts-utils'
import { callTurnToConfigFn } from 'util/turnto-utils'
import domUtils from 'util/dom-utils'
import globalConfig from 'init/global.config'
import globalStore, { Store } from 'init/global-store'
import { localizedThousandsInteger } from 'util/localization-utils'
import logger from 'util/logger'
import BasicReview from 'components/review/BasicReview.vue'
import PaginateMixin from 'mixins/PaginateMixin.vue'
import reviewsStore, { MIN_REVIEWS_FOR_TOOLBAR } from 'store/reviews-store'
import GridLayoutDataMixin from 'layouts/grid/GridLayoutDataMixin.vue'
import { RestError } from 'services/api-service'
import Review from 'components/review/Review.vue'
import reviewsListConfig, { RelatedReviewsDisplayTypes }
  from 'widgets/reviews-list/reviews-list-widget.config'
import reviewsListService from 'services/api/reviews-list'
import ReviewsListUtilities from 'components/reviews-list/ReviewsListUtilities.vue'
import trackingService from 'services/tracking-service'
import reviewsListUtils from 'util/reviews-list-utils'
import visibleObserver from 'util/visible-observer'

export default {
  data() {
    return {
      // gets updated when list count changes and focus doesn't go to heading (see template)
      ariaHeadingText: '',
      filterTotal: 0,
      originalReviews: new Map<number, unknown>(), // holds the review by id when translated
      previousSku: '',
      reviews: [],
      reviewsStore,
      showMoreDisabled: false,
      globalStoreState: globalStore.state,
      total: 0
    }
  },
  emits: ['openMediaModal', 'reviewsLoaded'],
  components: {
    'tt-basic-review': BasicReview,
    'tt-review': Review,
    'tt-reviews-list-utilities': ReviewsListUtilities
  },
  props: {
    showFullReview: {
      type: Boolean,
      default: true
    },
    widgetIsInitiallyHidden: {
      type: Boolean,
      default: false
    }
  },
  mixins: [GridLayoutDataMixin, PaginateMixin],
  computed: {
    currentSku(): string {
      return (this.globalStoreState as Store).sku
    },
    enableFrontEndReviewTranslation() {
      return reviewsListConfig.enableFrontEndReviewTranslation
    },
    filtersQueryString() {
      return this.reviewsStore.filterStore.encodedFiltersUrl
    },
    hasMoreRslts() {
      // the "hasMore" in paginate-mixin does not take applied filters into account
      if (this.isFiltered) {
        return this.reviews.length < this.filterTotal
      }
      return this.hasMore
    },
    hasSearchTerm() {
      return this.reviewsStore.trimmedSearchTerm.length > 0
    },
    heading() {
      const totalItems = this.totalReviews
      const numPages = this.numberOfPages
      const level = reviewsListConfig.headingLevel
      if (numPages <= 1) {
        const total = this.isFiltered ? this.filterTotal : this.total
        const text = this.$msg('reviewsList.numberOfReviews', [total], total)
        return { ariaText: text, text, level }
      }
      const startItem = reviewsListConfig.useShowMore ? 1 : this.offset + 1
      let endItem = this.reviews.length // when using "show more"
      if (!reviewsListConfig.useShowMore) {
        const itemsPerPage = this.offset === 0 ? this.initialPageSize : this.pageSize
        endItem = this.currentPage < numPages ? ((startItem - 1) + itemsPerPage) : totalItems
      }
      const localizedStart = localizedThousandsInteger(startItem)
      const localizedEnd = localizedThousandsInteger(endItem)
      const localizedTotal = localizedThousandsInteger(totalItems)
      return {
        ariaText: this.$msg('reviewsList.numberOfReviews.paged.aria',
          [localizedStart, localizedEnd, localizedTotal], totalItems),
        text: this.$msg('reviewsList.numberOfReviews.paged',
          [localizedStart, localizedEnd, localizedTotal], totalItems),
        level
      }
    },
    isRelatedIncluded() {
      return reviewsListConfig.relatedReviewsDisplay === RelatedReviewsDisplayTypes.INCLUDED
    },
    mediaSize() {
      return reviewsListConfig.breakpoints[this.currentLayoutIndex || 0].mediaSize
    },
    showUtilities() {
      // not to be shown if less than the min number of reviews or filters have been applied
      return (this.total >= MIN_REVIEWS_FOR_TOOLBAR || this.isFiltered) && this.showFullReview
    },
    sortBy() {
      return this.reviewsStore.sort
    },
    truncateLength() {
      return reviewsListConfig.breakpoints[this.currentLayoutIndex || 0].truncateLength
    },
    searchTerm() {
      return this.reviewsStore.trimmedSearchTerm
    },
    isFiltered() {
      return this.reviewsStore.filterStore.appliedFilters.length > 0 ||
        !!this.reviewsStore.trimmedSearchTerm
    },
    totalReviews() {
      return this.isFiltered ? this.filterTotal : this.total
    },
    numberOfPages() {
      const totalItems = this.totalReviews
      if (this.pageSize === 0 || totalItems === 0) {
        return 0
      }
      if (totalItems <= this.initialPageSize) {
        return 1
      }
      const totalLessInitialPageSize = totalItems - this.initialPageSize
      return Math.ceil(totalLessInitialPageSize / this.pageSize) + (this.initialPageSize ? 1 : 0)
    },
    noFilterResults() {
      return this.isFiltered && this.filterTotal === 0
    },
    showBasicReviews() {
      return reviewsListConfig.enableLazyLoading && this.widgetIsInitiallyHidden &&
        !this.showFullReview
    },
    showPurchaserBadge() {
      return reviewsListConfig.showPurchaserBadge
    },
    useShowMore() {
      return reviewsListConfig.useShowMore
    }
  },
  methods: {
    fetchData: debounce(function cb(isPageChange) {
      if (this.currentSku && (this.offset === 0 || this.offset <= this.totalReviews)) {
        const limit =
          this.offset === 0 ? this.initialPageSize : this.pageSize
        if (reviewsListConfig.useShowMore && this.offset !== 0) {
          this.showMoreDisabled = true
        }
        const isFirstCall = this.currentSku !== this.previousSku
        let sort = !this.hasSearchTerm ? this.sortBy : 'NONE'
        const sortLocales = window.turnToConfig?.sortLocales ?? []
        if (sort === 'LOCAL' && Array.isArray(sortLocales) && sortLocales.length > 0) {
          sort = encodeURIComponent(`${sort}:${sortLocales}`)
        }
        reviewsListService.reviewsList({
          sku: this.currentSku,
          offset: this.offset,
          limit,
          filters: this.filtersQueryString,
          sort,
          includeRelated: this.isRelatedIncluded,
          includeFilters: isFirstCall, // only include 1st time
          searchText: this.hasSearchTerm ? encodeURIComponent(this.searchTerm) : ''
        }).then((data) => {
          this.previousSku = this.currentSku
          globalStore.catItemId = data.catalogItemId
          globalStore.parentCategoryIds = data.parentCategoryIds
          globalStore.brand = data.brand
          globalStore.vpcId = data.vpcId
          if (isFirstCall && data.filters) {
            reviewsStore.filterStore.filters = data.filters
          }
          if (this.isFiltered) {
            this.filterTotal = data.total
          } else {
            this.total = data.total
            this.filterTotal = 0
          }
          if (reviewsListConfig.useShowMore && this.offset !== 0) {
            data.reviews.forEach((review) => {
              this.reviews.push(review)
            })
            this.showMoreDisabled = false
          } else {
            this.reviews = data.reviews
          }
          if (isFirstCall) {
            this.$emit('reviewsLoaded', data.reviews, data.catalogItemTitle,
              data.productReviewsJsonLdEnabled)

            this.queueWebEvents('load')
            const rootElem = this.$el
            visibleObserver.register(rootElem, () => {
              this.queueWebEvents('view')
              visibleObserver.unregister(rootElem)
            })
          }

          nextTick(() => {
            if (isPageChange) {
              // not first time, so may have paginated.  Will only scroll up, not down
              // TT-11837 - focus header after scroll. safeFocus in case client hides the header
              const focusFn = () => domUtils.safeFocus(() => this.$refs.headingText)
              domUtils
                .scrollUpToRef(this.$refs, 'reviewsList', globalConfig.additionalPagingScroll)
                .then(focusFn)
                .catch(focusFn)
            } else if (reviewsStore.updateAriaText) {
              this.ariaHeadingText = this.heading.ariaText
            }
            // make sure set to false after each update
            reviewsStore.updateAriaText = false

            // IF the client has these function defined under turnToConfig.reviewsList, they will be
            // called
            if (isFirstCall) {
              // if list is lazy loaded, will call onFinish when it is rendered
              // even for lazy "light", will wait until full list loaded
              // (see showFullReview in watch list below)
              if (!reviewsListConfig.enableLazyLoading) {
                callTurnToConfigFn('reviewsList', 'onFinish')
              }
            } else {
              callTurnToConfigFn('reviewsList', 'onUpdate')
            }
          })
        }).catch((e: RestError) => {
          logger.info('Error loading reviews list widget', e)
        })
      }
    }, 200),
    openMediaModal(media, startingIndex = 0, reviewId) {
      // passing it up one more level
      this.$emit('openMediaModal', media, startingIndex, reviewId)
    },
    translateReview(review, showOriginal: boolean) {
      reviewsListUtils.translateReview(review, this.originalReviews, this.reviews, showOriginal)
    },
    queueWebEvents(eventType) {
      const filters = this.reviewsStore.filterStore.appliedFilters.map((f) => {
        return { label: f.label, value: f.value }
      })
      let eventProperties
      if (eventType === 'load' || eventType === 'view') {
        eventProperties = { nr: this.reviews.length }
      } else {
        eventProperties = {
          nr: this.reviews.length,
          f: filters,
          st: this.sortBy,
          s: this.searchTerm,
          p: this.currentPage
        }
      }

      trackingService.queueEvent('reviews-list', eventType, this.currentSku, eventProperties)
    },
    queueSearchEvent: debounce(function cb() {
      if (this.searchTerm.length > 0) {
        this.queueWebEvents('search')
      }
    }, 200),
    searchTermFocus() {
      this.queueWebEvents('search-focus')
    }
  },
  created() {
    // initialPageSize and pageSize are in PaginateMixin
    this.initialPageSize = reviewsListConfig.initialPageSize || reviewsListConfig.pageSize
    this.pageSize = reviewsListConfig.pageSize
    // setLayoutConfig method in GridLayoutDataMixin
    this.setLayoutsConfig(reviewsListConfig.breakpoints)
    this.reviewsStore.sort = reviewsListConfig.defaultSortOption
    this.fetchData()
  },
  watch: {
    currentSku() {
      this.reviewsStore.sort = reviewsListConfig.defaultSortOption
      this.reviewsStore.searchTerm = ''
      this.reviewsStore.filterStore.clearFilters()
      // method in PaginateMixin
      this.onQueryChange()
    },
    searchTerm() {
      this.onQueryChange()
      this.queueSearchEvent()
    },
    sortBy() {
      this.queueWebEvents('sort')
    },
    filtersQueryString() {
      this.queueWebEvents('filter')
    },
    showFullReview() {
      // onFinish not called until list is fully loaded (when lazy loading enabled)
      nextTick(() => {
        callTurnToConfigFn('reviewsList', 'onFinish')
      })
    }
  }
}
</script>

<style lang="scss" src="./reviews-list.scss"></style>
